/**
 * Pure view helpers for the ThreadTriageBoard — the full-width triage surface
 * (queue | conversation | evidence) Scott picked as direction B. Kept out of
 * the component so the queue math and the filing-to-chips shaping are testable
 * without a DOM.
 */
import type {
  DoorThreadRead,
  ThreadCaseFileProposal,
  ThreadCaseItemProposal,
  ThreadInvestigation,
} from '../../services/qcpInvestigations';

export type TriageLane = 'routed' | 'cases' | 'done';

export const LANE_LABEL: Record<TriageLane, string> = {
  routed: 'Routed',
  cases: 'Cases',
  done: 'Done',
};

const LANE_STATUSES: Record<TriageLane, ReadonlyArray<DoorThreadRead['status']>> = {
  // `open` rides with routed: a thread the reporter tracked but has not
  // routed yet is still triage work, and hiding it would strand it.
  routed: ['routed', 'open'],
  cases: ['in_investigation'],
  // Both human closures: dismissed = "not an issue", completed = a real
  // issue, handled straight from triage (Jess's Done button, 2026-08-26).
  done: ['dismissed', 'completed'],
};

export function laneOf(thread: DoorThreadRead): TriageLane {
  if (thread.status === 'in_investigation') return 'cases';
  if (thread.status === 'dismissed' || thread.status === 'completed') return 'done';
  return 'routed';
}

/* ---------------------------------------------------------------- */
/* Whose queue is a thread in?                                       */
/* ---------------------------------------------------------------- */

/**
 * Name tokens used to recognise the door user in a free-text `routed_to`
 * ("Laura, Customer Service & Hadi"). Short tokens (< 3 chars) are dropped —
 * initials and "&" would false-match.
 */
export function nameTokens(name: string | null | undefined): string[] {
  return (name ?? '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length >= 3);
}

/**
 * Does this thread sit in the door user's own queue?
 *
 * `routed_to` is free text written by Merlin's router, so this is a token
 * match, not an id join. An empty `routed_to` counts as "mine": an unrouted
 * tracked thread is still triage work, and stranding it invisible would be
 * worse than showing it (same call as the board's routed-lane rule).
 */
export function isRoutedToUser(
  thread: Pick<DoorThreadRead, 'routed_to'>,
  userName: string | null | undefined,
): boolean {
  const routedTo = (thread.routed_to ?? '').trim().toLowerCase();
  if (!routedTo) return true;
  const tokens = nameTokens(userName);
  if (tokens.length === 0) return true; // unknown viewer → don't hide work
  return tokens.some((t) => routedTo.includes(t));
}

/**
 * The board's routed lane, scoped to the viewer: threads sent AWAY from
 * them drop out (Jess: "I already did that for this one. I don't know why
 * it's still showing" — Scott: "just make that disappear"). Other lanes are
 * untouched, and an unknown viewer sees everything (never hide work).
 */
export function laneThreadsForUser(
  threads: DoorThreadRead[],
  lane: TriageLane,
  userName: string | null | undefined,
): DoorThreadRead[] {
  const inLane = threadsInLane(threads, lane);
  if (lane !== 'routed') return inLane;
  return inLane.filter((t) => isRoutedToUser(t, userName));
}

export function threadsInLane(
  threads: DoorThreadRead[],
  lane: TriageLane,
): DoorThreadRead[] {
  const statuses = LANE_STATUSES[lane];
  return threads.filter((t) => statuses.includes(t.status));
}

export function laneCount(threads: DoorThreadRead[], lane: TriageLane): number {
  return threadsInLane(threads, lane).length;
}

/** Severity → the queue row's edge color class suffix (css handles the map). */
export function severityEdge(severity: string): 'critical' | 'high' | 'watch' {
  return severity === 'critical' || severity === 'high' ? severity : 'watch';
}

/** "2d" / "5h" / "now" — the queue row's age chip. */
export function ageLabel(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (mins < 60) return mins < 2 ? 'now' : `${mins}m`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours}h`;
  return `${Math.round(hours / 24)}d`;
}

/**
 * The join-key chips the evidence rail shows: the thread's own bridge_keys
 * merged with what the filing discovered (filing wins — it is the newer read).
 * `split_from` is lineage, not a join key — it gets its own affordance.
 */
export function evidenceBridgeKeys(thread: DoorThreadRead): Array<[string, string]> {
  const merged: Record<string, string> = { ...thread.bridge_keys };
  const filed = thread.investigation?.bridge_keys ?? {};
  for (const [k, v] of Object.entries(filed)) {
    if (k && v) merged[k] = v;
  }
  delete merged[SPLIT_FROM_KEY];
  return Object.entries(merged);
}

/* ---------------------------------------------------------------- */
/* Work state (posture)                                              */
/* ---------------------------------------------------------------- */

export const WORK_STATE_LABEL: Record<string, string> = {
  active: 'Active',
  escalating: 'Escalating',
  de_escalated: 'De-escalated',
  awaiting_evidence: 'Awaiting evidence',
  on_hold: 'On hold',
};

/** How the posture pill reads: hot postures alarm, parked postures calm. */
export const WORK_STATE_TONE: Record<string, 'hot' | 'calm' | 'parked'> = {
  escalating: 'hot',
  de_escalated: 'calm',
  awaiting_evidence: 'parked',
  on_hold: 'parked',
};

export function workStateLabel(state: string): string {
  return (
    WORK_STATE_LABEL[state] ??
    (state ? state.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()) : 'Active')
  );
}

/* ---------------------------------------------------------------- */
/* Chip-rail organisation                                            */
/* ---------------------------------------------------------------- */

// The identifiers a reader scans for first — everything else (the agent may
// invent keys) sits behind the "+N more" toggle so the rail stays a rail.
const CANONICAL_KEY_ORDER = [
  'customer_no',
  'sku',
  'product',
  'product_line',
  'batch',
  'lot',
  'order_no',
  'order_id',
  'jobsite',
  'zip',
  'state',
  'contact_email',
] as const;

export interface OrganizedChips {
  visible: Array<[string, string]>;
  overflow: Array<[string, string]>;
}

/**
 * Canonical join keys first (in scan order), the rest after, capped — the
 * QT-10003 rail carried 19 tags, half of them narrative. Display-side only:
 * what the agent wrote stays untouched.
 */
export function organizeChips(
  entries: Array<[string, string]>,
  cap = 6,
): OrganizedChips {
  const rank = (key: string): number => {
    const i = (CANONICAL_KEY_ORDER as readonly string[]).indexOf(key);
    return i === -1 ? CANONICAL_KEY_ORDER.length : i;
  };
  const sorted = [...entries].sort(
    (a, b) => rank(a[0]) - rank(b[0]) || a[0].localeCompare(b[0]),
  );
  return { visible: sorted.slice(0, cap), overflow: sorted.slice(cap) };
}

/* ---------------------------------------------------------------- */
/* Evidence grouping                                                 */
/* ---------------------------------------------------------------- */

export interface SignalDayGroup<S> {
  label: string;
  signals: S[];
}

/**
 * Group evidence cards by calendar day (newest day last, matching the rail's
 * chronological read). The Serapiglia thread carried 4 near-identical signals
 * per day — day headers + a collapse let the rail say "Aug 19 · 4 updates"
 * instead of repeating the same paragraph four times.
 */
export function groupSignalsByDay<S extends { created_at: string }>(
  signals: S[],
  dateLabel: (iso: string) => string,
): Array<SignalDayGroup<S>> {
  const groups: Array<SignalDayGroup<S>> = [];
  for (const sig of signals) {
    const label = dateLabel(sig.created_at) || 'Undated';
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.signals.push(sig);
    else groups.push({ label, signals: [sig] });
  }
  return groups;
}

/* ---------------------------------------------------------------- */
/* Split lineage                                                     */
/* ---------------------------------------------------------------- */
// When the investigator finds one thread bundling distinct problems, it
// splits them apart: each child is INSERTed with {"split_from": "<parent
// QT-no>"} in bridge_keys and the parent is rewritten down (Scott,
// 2026-08-20: "it needed the ability to break those back apart"). The
// convention lives in the investigator's instructions
// (services/agent_console/profiles.py step 5b) — these helpers make the
// lineage visible and navigable on the board instead of a bare grey chip.

const SPLIT_FROM_KEY = 'split_from';

/** The parent's QT-number when this thread was split out of one, else null. */
export function splitParentNo(thread: DoorThreadRead): string | null {
  const raw =
    thread.investigation?.bridge_keys?.[SPLIT_FROM_KEY] ??
    thread.bridge_keys?.[SPLIT_FROM_KEY];
  const value = typeof raw === 'string' ? raw.trim() : '';
  return value || null;
}

/** Threads that were split OUT of this one (their split_from names it). */
export function splitChildren(
  threads: DoorThreadRead[],
  thread: DoorThreadRead,
): DoorThreadRead[] {
  return threads.filter(
    (t) => t.id !== thread.id && splitParentNo(t) === thread.thread_no,
  );
}

/** Resolve a QT-number back to its thread (for "split from" navigation). */
export function threadByNo(
  threads: DoorThreadRead[],
  threadNo: string,
): DoorThreadRead | null {
  return threads.find((t) => t.thread_no === threadNo) ?? null;
}

/** The filing, or null when Merlin has not dug into this thread yet. */
export function filedInvestigation(
  thread: DoorThreadRead,
): ThreadInvestigation | null {
  const inv = thread.investigation;
  if (!inv || !inv.at_a_glance) return null;
  return inv;
}

/**
 * Merlin's proposed case file on the thread, or null when nothing is proposed
 * yet. Cancelled items are dropped (they will not seed the case); done items
 * stay so Jess sees what Merlin already ticked. Defensive on the read
 * boundary: the server normalises, but an older backend omits the field.
 */
export function proposedCaseFile(
  thread: Pick<DoorThreadRead, 'case_proposal'> | null,
): ThreadCaseFileProposal | null {
  const raw = thread?.case_proposal;
  if (!raw || typeof raw !== 'object') return null;
  const items = Array.isArray(raw.items)
    ? raw.items.filter(
        (i): i is ThreadCaseItemProposal =>
          !!i &&
          typeof i.label === 'string' &&
          i.label.trim().length > 0 &&
          (i.owner === 'us' || i.owner === 'customer') &&
          i.status !== 'cancelled',
      )
    : [];
  const answered = Array.isArray(raw.answered_questions)
    ? raw.answered_questions.filter(
        (q) => !!q && typeof q.question === 'string' && typeof q.answer === 'string',
      )
    : [];
  const notes = Array.isArray(raw.findings_notes)
    ? raw.findings_notes.filter((n): n is string => typeof n === 'string' && n.trim().length > 0)
    : [];
  const court =
    raw.whose_court === 'outstanding_on_us' ||
    raw.whose_court === 'waiting_on_customer' ||
    raw.whose_court === 'ready_to_close'
      ? raw.whose_court
      : '';
  const currentState = typeof raw.current_state === 'string' ? raw.current_state : '';
  if (items.length === 0 && answered.length === 0 && notes.length === 0 && !court && !currentState) {
    return null;
  }
  return {
    items,
    current_state: currentState,
    whose_court: court,
    answered_questions: answered,
    findings_notes: notes,
    updated_at: typeof raw.updated_at === 'string' ? raw.updated_at : null,
    updated_by: typeof raw.updated_by === 'string' ? raw.updated_by : null,
  };
}

/** Whose-court label for the proposal pill ("Ball: waiting on customer"). */
export const PROPOSED_COURT_LABEL: Record<
  Exclude<ThreadCaseFileProposal['whose_court'], ''>,
  string
> = {
  outstanding_on_us: 'on us',
  waiting_on_customer: 'waiting on customer',
  waiting_on_colleague: 'waiting on a colleague',
  ready_to_close: 'ready to close',
};

/** "looked into this at 6:32 AM" — the filing turn's byline. */
export function investigatedAtLabel(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** "Aug 5" — the evidence card's date chip. */
export function signalDateLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/* ---------------------------------------------------------------- */
/* "Merlin changed this since you looked"                            */
/* ---------------------------------------------------------------- */
// Scott, on the staging board: "we need to see when he has updates... some kind
// of exclamation point, like recently updated... so that if it changes from day
// to day, it shows." The useful question is not "is this recent?" but "has it
// moved since I last read it?" — a thread Jess read this morning should stop
// shouting, and the same thread should shout again the moment Merlin adds to it.
// Seen-marks are per-browser (localStorage): honest for a single-recipient
// pilot, and it means no server round-trip just to render a dot.

const SEEN_KEY = 'cm_qcp_thread_seen_v1';

type SeenMap = Record<string, string>;

function readSeen(): SeenMap {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? (parsed as SeenMap) : {};
  } catch {
    return {}; // a corrupt/blocked store must never break the board
  }
}

/** Remember that this thread has been read at its current revision. */
export function markThreadSeen(threadId: string, updatedAt: string): void {
  try {
    const seen = readSeen();
    seen[threadId] = updatedAt;
    localStorage.setItem(SEEN_KEY, JSON.stringify(seen));
  } catch {
    /* storage disabled — the badge just stays on, which is the safe way to be wrong */
  }
}

/**
 * Has Merlin touched this thread since it was last read here?
 *
 * A thread never read before counts as unseen — it is new to this reader.
 */
export function hasUnseenUpdate(
  thread: Pick<DoorThreadRead, 'id' | 'updated_at'>,
  seen: SeenMap = readSeen(),
): boolean {
  const mark = seen[thread.id];
  if (!mark) return true;
  const seenAt = new Date(mark).getTime();
  const changed = new Date(thread.updated_at).getTime();
  if (Number.isNaN(seenAt) || Number.isNaN(changed)) return true;
  return changed > seenAt;
}

/** Snapshot of what has been read, so a list renders from one read. */
export function seenSnapshot(): SeenMap {
  return readSeen();
}

/** How many threads in a set have moved since they were last read. */
export function unseenCount(threads: DoorThreadRead[]): number {
  const seen = readSeen();
  return threads.filter((t) => hasUnseenUpdate(t, seen)).length;
}

/* ---------------------------------------------------------------- */
/* Reopened threads + notes after closure (Scott, 2026-09-08)        */
/* ---------------------------------------------------------------- */

type ReopenFields = Partial<
  Pick<
    DoorThreadRead,
    | 'status'
    | 'reopened_at'
    | 'reopened_count'
    | 'reopened_from_status'
    | 'last_closed_at'
    | 'post_closure_update_at'
    | 'routing_reason'
    | 'work_state_note'
  >
>;

/** A live thread that had been closed and came back (router recurrence, or
 *  the recipient's own Reopen). Older payloads without the field read false. */
export function isReopened(thread: ReopenFields): boolean {
  return (
    (thread.status === 'routed' || thread.status === 'open') &&
    Boolean(thread.reopened_at)
  );
}

/** A CLOSED thread Merlin annotated without reopening it. */
export function hasPostClosureNote(thread: ReopenFields): boolean {
  return (
    (thread.status === 'completed' || thread.status === 'dismissed') &&
    Boolean(thread.post_closure_update_at)
  );
}

const HAND_PREFIX = 'Reopened by hand';

/** "Sep 2" — the closure/reopen dates on Done rows and reopen lines. */
export function shortDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * "Reopened · you marked this Done on Sep 2 · Merlin: the backorder misses
 * persisted…" — the line under a reopened queue row. The router's
 * routing_reason is written for the recipient (prompt rule); a by-hand reopen
 * reads as such with its note. `withReason: false` leaves the reason out
 * where the page already prints it under "Why it came to you" (proposal 3,
 * kanban 4d4a8707: the same text never twice on one screen).
 */
export function reopenedLine(
  thread: ReopenFields,
  { withReason = true }: { withReason?: boolean } = {},
): string {
  if (!thread.reopened_at) return '';
  const closedOn = shortDate(thread.last_closed_at);
  const was =
    thread.reopened_from_status === 'dismissed'
      ? `you dismissed this${closedOn ? ` on ${closedOn}` : ''}`
      : thread.reopened_from_status === 'completed'
        ? `you marked this Done${closedOn ? ` on ${closedOn}` : ''}`
        : 'it had been closed';
  const reason = (thread.routing_reason ?? '').trim();
  const firstLine = reason.split(/\r?\n/)[0]?.trim() ?? '';
  const times =
    (thread.reopened_count ?? 0) > 1 ? ` (${thread.reopened_count}× now)` : '';
  if (!withReason) {
    return `Reopened${firstLine.startsWith(HAND_PREFIX) ? ' by hand' : ''}${times} · ${was}`;
  }
  if (firstLine.startsWith(HAND_PREFIX)) {
    const note = firstLine.slice(HAND_PREFIX.length).replace(/^[\s—:-]+/, '').trim();
    return `Reopened by hand${times} · ${was}${note ? ` · ${note}` : ''}`;
  }
  return `Reopened${times} · ${was}${firstLine ? ` · Merlin: ${firstLine}` : ''}`;
}

/** "Merlin added a note after closure" line for a Done/Dismissed row. */
export function postClosureLine(thread: ReopenFields): string {
  if (!hasPostClosureNote(thread)) return '';
  const note = (thread.work_state_note ?? '').trim();
  return note
    ? `Merlin added a note after closure: ${note}`
    : 'Merlin updated this after closure';
}
